import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import cookie from "js-cookie";
import { getTheme } from "Theme";
import { AREAS } from "./content";
import { SIZES } from "assets/tutorial/sizes";

// The tutorial page (/faq): a contents sidebar on the left (areas that expand
// into their sections, following the section you're reading) and the content
// on the right. All the text and screenshots are in content.js.

const theme = getTheme();
const MOBILE = "@media (max-width: 900px)";

const Page = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.PAGE_BACKGROUND};
  color: ${theme.BLACK};
  ${MOBILE} {
    flex-direction: column;
  }
`;

const Sidebar = styled.nav`
  position: sticky;
  top: 0;
  align-self: flex-start;
  flex: 0 0 270px;
  height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 24px 12px 40px 16px;
  background: white;
  border-right: 1px solid #e3e6f3;
  ${MOBILE} {
    position: sticky;
    z-index: 5;
    flex: none;
    width: 100%;
    height: auto;
    max-height: ${(p) => (p.$open ? "70vh" : "none")};
    padding: 10px 16px;
    border-right: none;
    border-bottom: 1px solid #e3e6f3;
  }
`;

const Brand = styled.a`
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: ${theme.BLACK};
  text-decoration: none;
  margin: 0 8px 4px;
`;

const Back = styled.a`
  display: block;
  font-size: 14px;
  color: ${theme.MAIN_COLOR};
  margin: 0 8px 18px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  ${MOBILE} {
    margin-bottom: 8px;
  }
`;

const MenuToggle = styled.button`
  display: none;
  ${MOBILE} {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 10px 8px;
    border: 1px solid #d5daf0;
    border-radius: 8px;
    background: ${theme.PAGE_BACKGROUND};
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
`;

const Toc = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  ${MOBILE} {
    display: ${(p) => (p.$open ? "block" : "none")};
    margin-top: 8px;
  }
`;

const AreaButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 8px;
  border: none;
  border-radius: 6px;
  background: ${(p) => (p.$active ? "#eef3ff" : "transparent")};
  color: ${theme.BLACK};
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: #eef3ff;
  }
  &:focus-visible {
    outline: 2px solid ${theme.MAIN_COLOR};
  }
`;

const Chevron = styled.span`
  display: inline-block;
  font-size: 11px;
  color: #7a7f99;
  transition: transform 150ms ease;
  transform: rotate(${(p) => (p.$open ? 90 : 0)}deg);
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SectionList = styled.ul`
  list-style: none;
  margin: 2px 0 8px;
  padding: 0 0 0 10px;
  border-left: 2px solid #e3e6f3;
  margin-left: 14px;
`;

const SectionLink = styled.a`
  display: block;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 14px;
  color: #3d4257;
  text-decoration: none;
  ${(p) =>
    p.$active &&
    css`
      color: ${theme.MAIN_COLOR};
      font-weight: 700;
      background: #eef3ff;
    `}
  &:hover {
    background: #eef3ff;
  }
`;

const Main = styled.main`
  flex: 1;
  min-width: 0;
  padding: 32px 48px 120px;
  ${MOBILE} {
    padding: 16px 16px 80px;
  }
`;

const Inner = styled.div`
  max-width: 880px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 34px;
  margin: 0 0 6px;
  ${MOBILE} {
    font-size: 26px;
  }
`;

const Lead = styled.p`
  font-size: 16px;
  color: #4a4f66;
  margin: 0 0 28px;
`;

const AreaTitle = styled.h2`
  font-size: 26px;
  margin: 48px 0 8px;
  padding-bottom: 8px;
  border-bottom: 4px solid ${theme.MAIN_COLOR};
  scroll-margin-top: 16px;
  ${MOBILE} {
    scroll-margin-top: 70px;
  }
`;

const Section = styled.section`
  scroll-margin-top: 16px;
  padding-top: 8px;
  ${MOBILE} {
    scroll-margin-top: 70px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 21px;
  margin: 28px 0 12px;
`;

const SubTitle = styled.h4`
  font-size: 17px;
  margin: 26px 0 8px;
`;

const Para = styled.p`
  font-size: 15.5px;
  line-height: 1.6;
  margin: 0 0 12px;
`;

const Step = styled.figure`
  margin: 0 0 22px;
  padding: 14px 16px 16px;
  background: white;
  border: 1px solid #e3e6f3;
  border-radius: 10px;
`;

const StepText = styled.figcaption`
  display: flex;
  gap: 10px;
  font-size: 15.5px;
  line-height: 1.55;
  margin-bottom: 12px;
`;

const StepNumber = styled.span`
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${theme.MAIN_COLOR};
  color: white;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Shot = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  border: 1px solid #d5daf0;
  border-radius: 6px;
`;

const Formula = styled.div`
  margin: 4px 0 16px;
  padding: 14px 18px;
  background: #f3f6ff;
  border-left: 4px solid ${theme.MAIN_COLOR};
  border-radius: 6px;
  font-family: "Cambria Math", "STIX Two Math", Cambria, Georgia, serif;
  font-size: 17px;
  line-height: 1.8;
  white-space: pre-wrap;
  overflow-x: auto;
`;

const Where = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: #4a4f66;
  font-family: system-ui, sans-serif;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin: 4px 0 18px;
  border: 1px solid #e3e6f3;
  border-radius: 8px;
  background: white;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 14px;
  th {
    background: ${theme.MAIN_COLOR};
    color: white;
    text-align: left;
    padding: 9px 12px;
    font-weight: 600;
    white-space: nowrap;
  }
  td {
    padding: 8px 12px;
    border-top: 1px solid #eef0f8;
    vertical-align: top;
  }
  tr:last-child td {
    font-weight: ${(p) => (p.$totalRow ? 700 : "inherit")};
  }
`;

const Code = styled.pre`
  margin: 4px 0 16px;
  padding: 12px 16px;
  background: #1f2330;
  color: #e8ebf5;
  border-radius: 8px;
  font-size: 13.5px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre;
`;

const InlineCode = styled.code`
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.9em;
  background: #eceff8;
  padding: 1px 5px;
  border-radius: 4px;
`;

const Note = styled.div`
  margin: 4px 0 18px;
  padding: 12px 16px;
  background: #fff8e1;
  border: 1px solid #f3dfa0;
  border-radius: 8px;
  font-size: 15px;
  line-height: 1.55;
`;

const Bullets = styled.ul`
  margin: 0 0 14px;
  padding-left: 22px;
  li {
    font-size: 15.5px;
    line-height: 1.6;
    margin-bottom: 4px;
  }
`;

const Numbered = styled.ol`
  margin: 0 0 14px;
  padding-left: 22px;
  li {
    font-size: 15.5px;
    line-height: 1.6;
    margin-bottom: 4px;
  }
`;

// **bold** and `code` inside text
const rich = (text) =>
  String(text)
    .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
    .map((part, i) =>
      part.startsWith("**") ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : part.startsWith("`") ? (
        <InlineCode key={i}>{part.slice(1, -1)}</InlineCode>
      ) : (
        <Fragment key={i}>{part}</Fragment>
      ),
    );

const Block = ({ block, stepNumber }) => {
  if (block.step !== undefined)
    return (
      <Step>
        <StepText>
          <StepNumber aria-hidden="true">{stepNumber}</StepNumber>
          <span>{rich(block.step)}</span>
        </StepText>
        <Shot
          src={block.img}
          alt={block.alt || ""}
          width={SIZES[block.img]?.[0]}
          height={SIZES[block.img]?.[1]}
          loading="lazy"
        />
      </Step>
    );
  if (block.p) return <Para>{rich(block.p)}</Para>;
  if (block.h) return <SubTitle>{block.h}</SubTitle>;
  if (block.note) return <Note>{rich(block.note)}</Note>;
  if (block.code) return <Code>{block.code}</Code>;
  if (block.list)
    return (
      <Bullets>
        {block.list.map((item, i) => (
          <li key={i}>{rich(item)}</li>
        ))}
      </Bullets>
    );
  if (block.steps)
    return (
      <Numbered>
        {block.steps.map((item, i) => (
          <li key={i}>{rich(item)}</li>
        ))}
      </Numbered>
    );
  if (block.formula)
    return (
      <Formula role="math" aria-label={block.formula}>
        {block.formula}
        {block.where && (
          <Where>
            {block.where.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </Where>
        )}
      </Formula>
    );
  if (block.table) {
    const { head, rows } = block.table;
    return (
      <TableWrap>
        <Table $totalRow={!!block.table.total}>
          <thead>
            <tr>
              {head.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{rich(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    );
  }
  return null;
};

const SectionBody = ({ blocks }) => {
  let n = 0;
  return blocks.map((block, i) => (
    <Block key={i} block={block} stepNumber={block.step !== undefined ? ++n : undefined} />
  ));
};

const areaOf = (sectionId) =>
  AREAS.find((a) => a.id === sectionId || a.sections.some((s) => s.id === sectionId));

const Faq = () => {
  const firstSection = AREAS[0].sections[0].id;
  const initial = (window.location.hash || "").slice(1);
  const [active, setActive] = useState(initial && areaOf(initial) ? initial : firstSection);
  const [expanded, setExpanded] = useState(() => new Set([areaOf(active)?.id]));
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollingTo = useRef(null);
  const loggedIn = !!cookie.get("token");

  const sectionIds = useMemo(
    () => AREAS.flatMap((a) => a.sections.map((s) => s.id)),
    [],
  );

  // open the area holding the section you're reading
  useEffect(() => {
    const area = areaOf(active);
    if (area) setExpanded((old) => (old.has(area.id) ? old : new Set([...old, area.id])));
  }, [active]);

  // follow the section in view: the last one whose heading has reached the
  // top of the window (screenshots load late and move things, so this is
  // worked out from where the sections are now rather than from events)
  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      if (scrollingTo.current) return;
      let current = sectionIds[0];
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      // a short last section can't scroll up to the top
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atBottom) current = sectionIds[sectionIds.length - 1];
      setActive(current);
    };
    const onScroll = () => {
      if (frame == null) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    window.addEventListener("load", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("load", onScroll);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [sectionIds]);

  // a link to a section (#section-id) opens at it
  useEffect(() => {
    if (initial) document.getElementById(initial)?.scrollIntoView();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = (id) => (event) => {
    event.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    scrollingTo.current = id;
    const area = areaOf(id);
    if (area) setActive(area.sections.some((s) => s.id === id) ? id : area.sections[0].id);
    el.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    window.history.replaceState(null, "", `#${id}`);
    setMenuOpen(false);
    setTimeout(() => (scrollingTo.current = null), 800);
  };

  const toggle = (areaId) => {
    setExpanded((old) => {
      const next = new Set(old);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const activeArea = areaOf(active);
  const activeTitle = activeArea?.sections.find((s) => s.id === active)?.title;

  return (
    <Page>
      <Sidebar aria-label="Tutorial contents" $open={menuOpen}>
        <Brand href="#top" onClick={goTo("top")}>
          TCS Tutorial
        </Brand>
        <Back href={loggedIn ? "/dashboard" : "/"}>
          ← Back to {loggedIn ? "TCS" : "the home page"}
        </Back>
        <MenuToggle
          type="button"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span>Contents{activeTitle ? `: ${activeTitle}` : ""}</span>
          <Chevron $open={menuOpen}>▶</Chevron>
        </MenuToggle>
        <Toc $open={menuOpen}>
          {AREAS.map((area) => {
            const open = expanded.has(area.id);
            return (
              <li key={area.id}>
                <AreaButton
                  type="button"
                  aria-expanded={open}
                  $active={activeArea?.id === area.id}
                  onClick={() => toggle(area.id)}
                >
                  {area.title}
                  <Chevron $open={open}>▶</Chevron>
                </AreaButton>
                {open && (
                  <SectionList>
                    {area.sections.map((section) => (
                      <li key={section.id}>
                        <SectionLink
                          href={`#${section.id}`}
                          $active={active === section.id}
                          aria-current={active === section.id ? "location" : undefined}
                          onClick={goTo(section.id)}
                        >
                          {section.title}
                        </SectionLink>
                      </li>
                    ))}
                  </SectionList>
                )}
              </li>
            );
          })}
        </Toc>
      </Sidebar>

      <Main>
        <Inner>
          <Title id="top">Timecard Solutions Tutorial</Title>
          <Lead>
            How to use each part of TCS, step by step. Pick an area on the left;
            the red boxes in each screenshot show where to click.
          </Lead>
          {AREAS.map((area) => (
            <Fragment key={area.id}>
              <AreaTitle id={area.id}>{area.title}</AreaTitle>
              {area.sections.map((section) => (
                <Section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`}>
                  <SectionTitle id={`${section.id}-title`}>{section.title}</SectionTitle>
                  <SectionBody blocks={section.blocks} />
                </Section>
              ))}
            </Fragment>
          ))}
        </Inner>
      </Main>
    </Page>
  );
};

export default Faq;
